import { useNavigate } from "react-router-dom";

/**
 * Renders a user's name (or arbitrary children) as a keyboard-accessible
 * clickable element that navigates to `/u/:handle` (preferred) or `/u/:id`.
 * Safe to use INSIDE another <Link> — `stopPropagation` + `preventDefault`
 * keep the outer link from swallowing the click, and we avoid nested <a> tags.
 *
 * Falls back to a plain span when neither `userId` nor `handle` is provided.
 *
 * Two modes:
 *   1. Text mode (default): pass `name` (and optional `prefix`) → renders the name.
 *   2. Wrapper mode: pass `children` → wraps them (e.g. Avatar) with navigation.
 */
export default function UserNameLink({ userId, handle, name, className, prefix = "", testid, showUnderline = true, children }) {
  const navigate = useNavigate();
  const key = handle || userId;

  if (!key) {
    return <span className={className} data-testid={testid}>{children || `${prefix}${name || "User"}`}</span>;
  }
  const go = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/u/${key}`);
  };
  const label = children || `${prefix}${name || "User"}`;
  const hoverCls = children ? "" : (showUnderline ? " hover:underline underline-offset-2" : "");
  return (
    <span
      role="link"
      tabIndex={0}
      onClick={go}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") go(e); }}
      className={`cursor-pointer ${children ? "" : "hover:text-[#0D9488]"}${hoverCls} ${className || ""}`}
      data-testid={testid || `user-name-link-${key}`}
      title={`View @${handle || name || "user"}'s profile`}
    >
      {label}
    </span>
  );
}
